import { useState, useCallback, useMemo } from "react"
import {Calculator, ChevronDown, TrendingDown, Target, TrendingUp, Flame, Beef, Drumstick, Wheat} from 'lucide-react'
import ExportButtons from "./ExportButtons"
import SaveHistoryButton from "./SaveHistoryButton"
import { BMRTooltip, TDEETooltip, BMITooltip } from "./Tooltip"
import type { HistoryPayload } from "@/hooks/useCalculationHistory"

type Gender = "male" | "female"
type Unit = "metric" | "imperial"
type ActivityLevel = "sedentary" | "light" | "moderate" | "active" | "extra"
type Goal = "lose" | "maintain" | "gain"

interface Results {
  bmr: number
  tdee: number
  adjustedCalories: number
  fatCaloriesMin: number
  fatCaloriesMax: number
  fatGramsMin: number
  fatGramsMax: number
  bmi: number
  proteinGrams: number
  proteinCalories: number
  carbGramsMin: number
  carbGramsMax: number
  carbCaloriesMin: number
  carbCaloriesMax: number
}

const ACTIVITY_MULTIPLIERS: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  extra: 1.9,
}

const ACTIVITY_LABELS: Record<ActivityLevel, string> = {
  sedentary: "Sedentary (little or no exercise)",
  light: "Lightly Active (1-3 days/week)",
  moderate: "Moderately Active (3-5 days/week)",
  active: "Very Active (6-7 days/week)",
  extra: "Extra Active (athlete / physical job)",
}

const GOAL_CONFIG: Record<Goal, { label: string; icon: typeof Target; color: string; modifier: number; proteinMultiplier: number }> = {
  lose: { label: "Lose Weight", icon: TrendingDown, color: "text-blue-600 bg-blue-50 border-blue-200", modifier: 0.8, proteinMultiplier: 2.0 },
  maintain: { label: "Maintain Weight", icon: Target, color: "text-emerald-600 bg-emerald-50 border-emerald-200", modifier: 1.0, proteinMultiplier: 1.6 },
  gain: { label: "Gain Muscle", icon: TrendingUp, color: "text-amber-600 bg-amber-50 border-amber-200", modifier: 1.15, proteinMultiplier: 2.2 },
}

export default function FatCalculator() {
  const [gender, setGender] = useState<Gender>("male")
  const [age, setAge] = useState("")
  const [unit, setUnit] = useState<Unit>("metric")
  const [weight, setWeight] = useState("")
  const [heightCm, setHeightCm] = useState("")
  const [heightFt, setHeightFt] = useState("")
  const [heightIn, setHeightIn] = useState("")
  const [activity, setActivity] = useState<ActivityLevel>("moderate")
  const [goal, setGoal] = useState<Goal>("maintain")
  const [results, setResults] = useState<Results | null>(null)
  const [showResults, setShowResults] = useState(false)

  const calculate = useCallback(() => {
    const ageNum = parseInt(age)
    let weightKg: number
    let heightCmNum: number

    if (unit === "metric") {
      weightKg = parseFloat(weight)
      heightCmNum = parseFloat(heightCm)
    } else {
      weightKg = parseFloat(weight) * 0.453592
      heightCmNum = (parseInt(heightFt) * 12 + parseInt(heightIn)) * 2.54
    }

    if (isNaN(ageNum) || isNaN(weightKg) || isNaN(heightCmNum) || ageNum <= 0 || weightKg <= 0 || heightCmNum <= 0) {
      return
    }

    // Mifflin-St Jeor Equation
    let bmr: number
    if (gender === "male") {
      bmr = 10 * weightKg + 6.25 * heightCmNum - 5 * ageNum + 5
    } else {
      bmr = 10 * weightKg + 6.25 * heightCmNum - 5 * ageNum - 161
    }

    const tdee = bmr * ACTIVITY_MULTIPLIERS[activity]
    const adjustedCalories = tdee * GOAL_CONFIG[goal].modifier

    // Fat: 20%-35% of adjusted calories
    const fatCaloriesMin = adjustedCalories * 0.2
    const fatCaloriesMax = adjustedCalories * 0.35
    const fatGramsMin = fatCaloriesMin / 9
    const fatGramsMax = fatCaloriesMax / 9

    // BMI = weight(kg) / height(m)^2
    const heightM = heightCmNum / 100
    const bmi = weightKg / (heightM * heightM)

    // Protein: goal-based g/kg of body weight (1 gram protein = 4 calories)
    const proteinGrams = weightKg * GOAL_CONFIG[goal].proteinMultiplier
    const proteinCalories = proteinGrams * 4

    // Carbs: remaining calories after fat + protein (1 gram carb = 4 calories)
    // Use midpoint of fat range for carb calc, then show a range
    const carbCaloriesMin = adjustedCalories - fatCaloriesMax - proteinCalories
    const carbCaloriesMax = adjustedCalories - fatCaloriesMin - proteinCalories
    const carbGramsMin = Math.max(0, carbCaloriesMin / 4)
    const carbGramsMax = Math.max(0, carbCaloriesMax / 4)

    setResults({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      adjustedCalories: Math.round(adjustedCalories),
      fatCaloriesMin: Math.round(fatCaloriesMin),
      fatCaloriesMax: Math.round(fatCaloriesMax),
      fatGramsMin: Math.round(fatGramsMin),
      fatGramsMax: Math.round(fatGramsMax),
      bmi: Math.round(bmi * 10) / 10,
      proteinGrams: Math.round(proteinGrams),
      proteinCalories: Math.round(proteinCalories),
      carbGramsMin: Math.round(carbGramsMin),
      carbGramsMax: Math.round(carbGramsMax),
      carbCaloriesMin: Math.round(Math.max(0, carbCaloriesMin)),
      carbCaloriesMax: Math.round(Math.max(0, carbCaloriesMax)),
    })
    setShowResults(true)
  }, [age, weight, heightCm, heightFt, heightIn, unit, gender, activity, goal])

  const isFormValid = () => {
    if (!age || !weight) return false
    if (unit === "metric" && !heightCm) return false
    if (unit === "imperial" && (!heightFt || !heightIn)) return false
    return true
  }

  const historyPayload = useMemo<HistoryPayload | null>(() => {
    if (!results) return null
    return {
      calculatorType: "fat",
      summary: `Fat ${results.fatGramsMin}–${results.fatGramsMax} g/day · Protein ${results.proteinGrams} g · ${GOAL_CONFIG[goal].label}`,
      details: {
        goal,
        gender,
        age,
        activity,
        fatGramsMin: results.fatGramsMin,
        fatGramsMax: results.fatGramsMax,
        proteinGrams: results.proteinGrams,
        carbGramsMin: results.carbGramsMin,
        carbGramsMax: results.carbGramsMax,
        bmr: results.bmr,
        tdee: results.tdee,
        adjustedCalories: results.adjustedCalories,
        bmi: results.bmi,
      },
      gender,
      age: parseInt(age) || 0,
      goal: GOAL_CONFIG[goal].label,
      activity: ACTIVITY_LABELS[activity],
    }
  }, [results, goal, gender, age, activity])

  const exportRows = useMemo(() => {
    if (!results) return []
    return [
      { label: "Daily Fat Intake (Min)", value: `${results.fatGramsMin} grams` },
      { label: "Daily Fat Intake (Max)", value: `${results.fatGramsMax} grams` },
      { label: "Fat Calories (Min)", value: `${results.fatCaloriesMin} kcal` },
      { label: "Fat Calories (Max)", value: `${results.fatCaloriesMax} kcal` },
      { label: "BMR (Basal Metabolic Rate)", value: `${results.bmr} kcal` },
      { label: "TDEE (Total Daily Energy)", value: `${results.tdee} kcal` },
      { label: `Adjusted Calories (${GOAL_CONFIG[goal].label})`, value: `${results.adjustedCalories} kcal` },
      { label: "Estimated BMI", value: `${results.bmi} kg/m²` },
      { label: "Protein Intake", value: `${results.proteinGrams} grams (${results.proteinCalories} kcal)` },
      { label: "Carb Intake (Min)", value: `${results.carbGramsMin} grams (${results.carbCaloriesMin} kcal)` },
      { label: "Carb Intake (Max)", value: `${results.carbGramsMax} grams (${results.carbCaloriesMax} kcal)` },
      { label: "Fat Range", value: "20% - 35% of daily calories" },
      { label: "Protein Ratio", value: `${GOAL_CONFIG[goal].proteinMultiplier} g/kg body weight` },
      { label: "Gender", value: gender === "male" ? "Male" : "Female" },
      { label: "Age", value: `${age} years` },
      { label: "Activity Level", value: ACTIVITY_LABELS[activity] },
      { label: "Fitness Goal", value: GOAL_CONFIG[goal].label },
    ]
  }, [results, goal, gender, age, activity])

  return (
    <section id="calculator" className="relative pt-6 pb-16 sm:pt-8 sm:pb-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200/80 mb-4 shadow-sm">
            <Calculator className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Free Tool</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2 tracking-tight">
            Calculate Your Daily Fat Intake
          </h2>
          <p className="text-slate-500 max-w-lg mx-auto">
            Enter your details below for a personalized fat requirement based on the Mifflin-St Jeor equation.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-200/80 shadow-premium p-6 sm:p-8">
            {/* Gender */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2.5">Gender</label>
              <div className="grid grid-cols-2 gap-3">
                {(["male", "female"] as Gender[]).map((g) => (
                  <button
                    key={g}
                    onClick={() => setGender(g)}
                    className={`py-3 px-4 rounded-xl text-sm font-semibold border-2 transition-all duration-200 ${
                      gender === g
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm ring-1 ring-emerald-500/20"
                        : "border-gray-200 bg-white text-slate-600 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {g === "male" ? "Male" : "Female"}
                  </button>
                ))}
              </div>
            </div>

            {/* Age */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2.5">Age (years)</label>
              <input
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="e.g. 30"
                min="1"
                max="120"
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none text-slate-800 placeholder:text-gray-400 transition-all duration-200"
              />
            </div>

            {/* Unit Toggle + Weight */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2.5">
                <label className="text-sm font-semibold text-slate-700">Weight</label>
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                  {(["metric", "imperial"] as Unit[]).map((u) => (
                    <button
                      key={u}
                      onClick={() => setUnit(u)}
                      className={`px-3 py-1 text-xs font-semibold rounded-md transition-all duration-200 ${
                        unit === u ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {u === "metric" ? "kg / cm" : "lbs / ft"}
                    </button>
                  ))}
                </div>
              </div>
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder={unit === "metric" ? "e.g. 75 kg" : "e.g. 165 lbs"}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none text-slate-800 placeholder:text-gray-400 transition-all duration-200"
              />
            </div>

            {/* Height */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2.5">Height</label>
              {unit === "metric" ? (
                <input
                  type="number"
                  value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)}
                  placeholder="e.g. 175 cm"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none text-slate-800 placeholder:text-gray-400 transition-all duration-200"
                />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="number"
                    value={heightFt}
                    onChange={(e) => setHeightFt(e.target.value)}
                    placeholder="Feet"
                    className="px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none text-slate-800 placeholder:text-gray-400 transition-all duration-200"
                  />
                  <input
                    type="number"
                    value={heightIn}
                    onChange={(e) => setHeightIn(e.target.value)}
                    placeholder="Inches"
                    className="px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none text-slate-800 placeholder:text-gray-400 transition-all duration-200"
                  />
                </div>
              )}
            </div>

            {/* Activity Level */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2.5">Activity Level</label>
              <div className="relative">
                <select
                  value={activity}
                  onChange={(e) => setActivity(e.target.value as ActivityLevel)}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none text-slate-800 appearance-none bg-white transition-all duration-200 cursor-pointer"
                >
                  {Object.entries(ACTIVITY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Fitness Goal */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-slate-700 mb-2.5">Fitness Goal</label>
              <div className="grid grid-cols-3 gap-3">
                {(Object.entries(GOAL_CONFIG) as [Goal, typeof GOAL_CONFIG[Goal]][]).map(([key, cfg]) => {
                  const Icon = cfg.icon
                  return (
                    <button
                      key={key}
                      onClick={() => setGoal(key)}
                      className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl text-xs sm:text-sm font-semibold border-2 transition-all duration-200 ${
                        goal === key
                          ? cfg.color + " shadow-sm ring-1 ring-inset"
                          : "border-gray-200 bg-white text-slate-600 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {cfg.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Calculate Button */}
            <button
              onClick={calculate}
              disabled={!isFormValid()}
              className="w-full py-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white font-bold text-base rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-emerald-glow hover:shadow-lg hover:shadow-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:from-emerald-500 disabled:hover:to-emerald-600 disabled:shadow-none active:scale-[0.98]"
            >
              Calculate My Fat Intake
            </button>

            {/* SEO supportive sentence */}
            <p className="mt-5 text-center text-sm text-slate-500 leading-relaxed">
              Our tool calculates your BMR and TDEE automatically to give you the most accurate fat gram requirements based on your body index.
            </p>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            {showResults && results ? (
              <div className="relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 sm:p-8 text-white shadow-premium-lg animate-fade-in-up grain-dark">
                <div className="relative flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center ring-1 ring-emerald-400/20">
                      <Beef className="w-4 h-4 text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: "'DM Sans', sans-serif" }}>Your Results</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <SaveHistoryButton payload={historyPayload} variant="dark" />
                    <ExportButtons
                      title="Fat Intake Calculator Results"
                      subtitle={`${GOAL_CONFIG[goal].label} | ${gender === "male" ? "Male" : "Female"}, Age ${age}`}
                      rows={exportRows}
                      accentColor="emerald"
                      note="1 gram of fat = 9 calories. Results based on the Mifflin-St Jeor equation. Consult a healthcare professional for personalized advice."
                    />
                  </div>
                </div>

                {/* Main Result */}
                <div className="relative bg-white/5 border border-white/10 rounded-xl p-5 mb-5 animate-count-up shadow-inner-soft">
                  <p className="text-sm text-gray-400 mb-1">Daily Fat Intake (Healthy Range)</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl sm:text-4xl font-extrabold text-gradient-emerald">
                      {results.fatGramsMin}–{results.fatGramsMax}
                    </span>
                    <span className="text-sm text-gray-400 ml-1">grams/day</span>
                  </div>
                </div>

                {/* Macro Breakdown: Fat / Protein / Carbs */}
                <div className="relative space-y-3 mb-5">
                  {/* Fat */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 transition-colors duration-200 hover:bg-white/[0.07]">
                    <div className="flex items-center gap-2 mb-2">
                      <Flame className="w-4 h-4 text-orange-400" />
                      <p className="text-sm text-gray-300 font-medium">Fat</p>
                      <span className="ml-auto text-[10px] text-gray-500 uppercase tracking-wider">20–35% of calories</span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <p className="text-xl font-bold text-white">
                        {results.fatGramsMin}–{results.fatGramsMax}<span className="text-sm font-normal text-gray-400 ml-1">g</span>
                      </p>
                      <p className="text-xs text-gray-500">{results.fatCaloriesMin}–{results.fatCaloriesMax} kcal</p>
                    </div>
                  </div>

                  {/* Protein */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 transition-colors duration-200 hover:bg-white/[0.07]">
                    <div className="flex items-center gap-2 mb-2">
                      <Drumstick className="w-4 h-4 text-rose-400" />
                      <p className="text-sm text-gray-300 font-medium">Protein</p>
                      <span className="ml-auto text-[10px] text-gray-500 uppercase tracking-wider">{GOAL_CONFIG[goal].proteinMultiplier} g/kg</span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <p className="text-xl font-bold text-white">
                        {results.proteinGrams}<span className="text-sm font-normal text-gray-400 ml-1">g</span>
                      </p>
                      <p className="text-xs text-gray-500">{results.proteinCalories} kcal</p>
                    </div>
                  </div>

                  {/* Carbs */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 transition-colors duration-200 hover:bg-white/[0.07]">
                    <div className="flex items-center gap-2 mb-2">
                      <Wheat className="w-4 h-4 text-amber-400" />
                      <p className="text-sm text-gray-300 font-medium">Carbs</p>
                      <span className="ml-auto text-[10px] text-gray-500 uppercase tracking-wider">Remaining calories</span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <p className="text-xl font-bold text-white">
                        {results.carbGramsMin}–{results.carbGramsMax}<span className="text-sm font-normal text-gray-400 ml-1">g</span>
                      </p>
                      <p className="text-xs text-gray-500">{results.carbCaloriesMin}–{results.carbCaloriesMax} kcal</p>
                    </div>
                  </div>
                </div>

                {/* Visual Macro Bar */}
                <div className="relative mb-5">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Macro Split (midpoint)</p>
                  {(() => {
                    const fatMid = (results.fatCaloriesMin + results.fatCaloriesMax) / 2
                    const carbMid = (results.carbCaloriesMin + results.carbCaloriesMax) / 2
                    const total = fatMid + results.proteinCalories + carbMid
                    const fatPct = total > 0 ? Math.round((fatMid / total) * 100) : 0
                    const protPct = total > 0 ? Math.round((results.proteinCalories / total) * 100) : 0
                    const carbPct = Math.max(0, 100 - fatPct - protPct)
                    return (
                      <>
                        <div className="flex h-3 rounded-full overflow-hidden gap-0.5 shadow-inner-soft">
                          <div className="bg-orange-400 rounded-l-full transition-all duration-500" style={{ width: `${fatPct}%` }} />
                          <div className="bg-rose-400 transition-all duration-500" style={{ width: `${protPct}%` }} />
                          <div className="bg-amber-400 rounded-r-full transition-all duration-500" style={{ width: `${carbPct}%` }} />
                        </div>
                        <div className="flex justify-between mt-1.5 text-[10px] text-gray-500">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />Fat {fatPct}%</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400 inline-block" />Protein {protPct}%</span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />Carbs {carbPct}%</span>
                        </div>
                      </>
                    )
                  })()}
                </div>

                {/* Estimated BMI & TDEE — discreet display */}
                <div className="relative grid grid-cols-2 gap-3 mb-5">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 text-center transition-colors duration-200 hover:bg-white/[0.07]">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 flex items-center justify-center gap-1">Estimated BMI <BMITooltip variant="dark" /></p>
                    <p className="text-lg font-bold text-white">{results.bmi}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">kg/m²</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-3.5 text-center transition-colors duration-200 hover:bg-white/[0.07]">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 flex items-center justify-center gap-1">Estimated TDEE <TDEETooltip variant="dark" /></p>
                    <p className="text-lg font-bold text-white">{results.tdee}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">kcal/day</p>
                  </div>
                </div>

                {/* Breakdown */}
                <div className="relative space-y-3 mb-5">
                  {[
                    { label: "BMR", fullLabel: "Basal Metabolic Rate", value: `${results.bmr} kcal`, tooltip: "bmr" as const },
                    { label: "TDEE", fullLabel: "Total Daily Energy", value: `${results.tdee} kcal`, tooltip: "tdee" as const },
                    { label: "Adjusted Calories", fullLabel: GOAL_CONFIG[goal].label, value: `${results.adjustedCalories} kcal`, tooltip: null },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between py-2 border-b border-white/5">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        {item.label} ({item.fullLabel})
                        {item.tooltip === "bmr" && <BMRTooltip variant="dark" />}
                        {item.tooltip === "tdee" && <TDEETooltip variant="dark" />}
                      </span>
                      <span className="text-sm font-semibold text-white">{item.value}</span>
                    </div>
                  ))}
                </div>

                {/* Info */}
                <div className="relative bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                  <p className="text-xs text-emerald-300 leading-relaxed">
                    <strong>Note:</strong> 1g fat = 9 kcal, 1g protein = 4 kcal, 1g carbs = 4 kcal. Protein is set at {GOAL_CONFIG[goal].proteinMultiplier}g/kg for {GOAL_CONFIG[goal].label.toLowerCase()}. Carbs fill remaining calories. Based on the Mifflin-St Jeor equation. Consult a healthcare professional for personalized advice.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-50 flex items-center justify-center shadow-sm">
                  <Calculator className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-slate-700 mb-2 tracking-tight" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  Your Results Will Appear Here
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Fill in the form and click "Calculate" to see your personalized daily fat intake recommendation.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
